import {messagesService} from '@dv5/api'
import {addWidgetLink, changeWidgetData, LinkPosition, useAppDispatch, Widget, WidgetLink} from '@dv5/domain'
import {MouseEvent, RefObject, useCallback, useEffect, useState} from "react"
import {CanvasMode} from "../../model/CanvasMode"
import {CanvasPosition} from "../../model/CanvasPosition"
import {DragState} from "../../model/DragState"
import {MousePosition} from "../../model/MousePosition"
import {ResizeDirection} from "../../model/ResizeDirection"
import {SelectionRect} from "../../model/SelectionRect"
import {canvasMoveService} from "../../services/canvasMoveService"
import {canvasService} from "../../services/canvasService"
import {widgetService} from "../../services/widgetService"
import {CLICK_DISTANCE_THRESHOLD, MIN_WIDGET_SIZE} from "../utils/constants"
import {getDraggedWidgets, moveWidgetPositions} from "../utils/dragWidgets"
import {processCanvasClick} from "../utils/processCanvasClick"
import {useCanvasSettings} from "./useCanvasSettings"

interface CreateWidgetLink {
    canvasId: string
    startWidgetId: string
    endWidgetId: string
    data: any
}

export interface UseCanvasMouseHandlersParams {
    mode: CanvasMode
    canvasId: string
    zoom: number
    scrollPosition: CanvasPosition
    layerIds: Array<string>
    showUseTemplateDialog: boolean
    canvasContent: { widgets: Array<any>, widgetLinks: Array<any> }
    canvasSelection: Array<string>
    onChangeSelection: (selection: Array<string>) => void
    onStartEdit: (widgetId: string) => void
    canvasRef: RefObject<HTMLDivElement | null>
    onChangeTool: (newTool: CanvasMode) => void,
    onSaveCoordinates: (canvasPosition: CanvasPosition) => void
    onChooseDocument: (canvasPosition: CanvasPosition, ctrlKey: boolean) => void
    onChooseTask: (canvasPosition: CanvasPosition, ctrlKey: boolean) => void
}

export interface UseCanvasMouseHandlersReturn {
    dragState: DragState
    setDragState: (dragState: DragState) => void
    handleMouseDown: (e: MouseEvent<HTMLDivElement>) => void
    handleMouseMove: (e: MouseEvent<HTMLDivElement>) => void
    handleMouseUp: (e: MouseEvent<HTMLDivElement>) => void
    startDrag: (widgetId: string, mousePosition: MousePosition, widgetLeft: number, widgetTop: number) => void
    startResize: (widgetId: string, mousePosition: MousePosition, widgetLeft: number, widgetTop: number, widgetWidth: number, widgetHeight: number, direction: ResizeDirection) => void
    startLinkCreation: (widgetId: string, position: LinkPosition) => void
    completeLinkCreation: (targetWidgetId: string, targetPosition: LinkPosition) => void
}

export const useCanvasMouseHandlers = ({
                                           mode,
                                           canvasId,
                                           zoom,
                                           scrollPosition,
                                           layerIds,
                                           showUseTemplateDialog,
                                           canvasContent,
                                           canvasSelection,
                                           onChangeSelection,
                                           onStartEdit,
                                           onChangeTool,
                                           canvasRef,
                                            onSaveCoordinates,
                                            onChooseDocument,
                                            onChooseTask,
                                       }: UseCanvasMouseHandlersParams): UseCanvasMouseHandlersReturn => {
    const dispatch = useAppDispatch()
    const settings = useCanvasSettings(canvasId)
    const alignToGrid = settings.alignToGrid
    const readOnly = settings.readOnly
    const getMousePosition = useCallback((position: MousePosition): MousePosition => {
        if (!canvasRef.current) return position
        const canvasPosition = widgetService.getCanvasPosition(
            position, canvasRef.current.getBoundingClientRect(), zoom, scrollPosition,
        )
        return {x: canvasPosition.left, y: canvasPosition.top}
    }, [canvasRef, zoom, scrollPosition])
    const [dragState, setDragState] = useState<DragState>({
        isDragging: false,
        dragInfo: null,
        selectionRect: null,
        resizeState: null,
        linkCreation: null,
        mousePosition: null,
        selectedLinkIds: [],
    })
    const getWidgetBounds = (widget: Widget) => ({
        left: widget.left,
        top: widget.top,
        right: widget.left + widget.width,
        bottom: widget.top + widget.height,
    })

    const rectsIntersect = (a: { left: number, top: number, right: number, bottom: number },
                            b: { left: number, top: number, right: number, bottom: number }) => {
        return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom)
    }

    const getWidgetsInRect = useCallback((rect: SelectionRect, widgets: Array<Widget>) => {
        const selLeft = Math.min(rect.startX, rect.endX)
        const selTop = Math.min(rect.startY, rect.endY)
        const selRight = Math.max(rect.startX, rect.endX)
        const selBottom = Math.max(rect.startY, rect.endY)

        return widgets.filter(widget => {
            const bounds = getWidgetBounds(widget)
            return rectsIntersect(bounds, {left: selLeft, top: selTop, right: selRight, bottom: selBottom})
        })
    }, [])

    const getLinksInRect = useCallback((rect: SelectionRect, widgetLinks: Array<any>, widgets: Array<Widget>) => {
        const selLeft = Math.min(rect.startX, rect.endX)
        const selTop = Math.min(rect.startY, rect.endY)
        const selRight = Math.max(rect.startX, rect.endX)
        const selBottom = Math.max(rect.startY, rect.endY)

        const getWidgetPosition = (widgetId: string) => {
            return widgets.find(w => w.id === widgetId)
        }

        return widgetLinks.filter(link => {
            const startWidget = getWidgetPosition(link.startWidgetId)
            const endWidget = getWidgetPosition(link.endWidgetId)
            if (!startWidget || !endWidget) return false

            const linkLeft = Math.min(startWidget.left, endWidget.left)
            const linkTop = Math.min(startWidget.top, endWidget.top)
            const linkRight = Math.max(startWidget.left + (startWidget.width || 0), endWidget.left + (endWidget.width || 0))
            const linkBottom = Math.max(startWidget.top + (startWidget.height || 0), endWidget.top + (endWidget.height || 0))

            return rectsIntersect(
                {left: linkLeft, top: linkTop, right: linkRight, bottom: linkBottom},
                {left: selLeft, top: selTop, right: selRight, bottom: selBottom}
            )
        }).map(link => link.id)
    }, [])

    const handleMouseDown = useCallback((e: MouseEvent<HTMLDivElement>) => {
        if (e.button !== 0) {
            return
        }

        if (mode === 'select' && canvasRef.current) {
            const {x, y} = getMousePosition({x: e.clientX, y: e.clientY})

            const clickedWidget = canvasContent.widgets.find(widget => {
                const bounds = getWidgetBounds(widget)
                return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom
            })

            if (!clickedWidget) {
                setDragState({
                    isDragging: false,
                    dragInfo: null,
                    selectionRect: {startX: x, startY: y, endX: x, endY: y},
                    resizeState: null,
                    linkCreation: dragState.linkCreation,
                    mousePosition: null,
                    selectedLinkIds: dragState.selectedLinkIds,
                })
                return
            }
        }

        const position: MousePosition = {x: e.clientX, y: e.clientY}
        canvasService.handleMouseDown(position)
    }, [mode, dragState, canvasContent.widgets, canvasRef, getMousePosition])

    const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
        if (dragState.selectionRect) {
            if (canvasRef.current) {
                const {x, y} = getMousePosition({x: e.clientX, y: e.clientY})
                setDragState({
                    ...dragState,
                    selectionRect: {...dragState.selectionRect, endX: x, endY: y},
                })
            }
            return
        }

        if (readOnly) return

        if (dragState.linkCreation && canvasRef.current) {
            const {x, y} = getMousePosition({x: e.clientX, y: e.clientY})
            setDragState({
                ...dragState,
                mousePosition: {x, y},
            })
            return
        }

        if (dragState.resizeState) {
            const rs = dragState.resizeState
            const position = getMousePosition({x: e.clientX, y: e.clientY})
            const widget = canvasContent.widgets.find(w => w.id === rs.widgetId)

            if (widget) {
                const deltaX = position.x - rs.startMousePosition.x
                const deltaY = position.y - rs.startMousePosition.y

                let newWidth = rs.initialWidgetSize.width
                let newHeight = rs.initialWidgetSize.height
                let newLeft = rs.initialWidgetPosition.left
                let newTop = rs.initialWidgetPosition.top

                if (rs.direction.includes('E')) {
                    newWidth = Math.max(MIN_WIDGET_SIZE, rs.initialWidgetSize.width + deltaX)
                }
                if (rs.direction.includes('W')) {
                    newWidth = Math.max(MIN_WIDGET_SIZE, rs.initialWidgetSize.width - deltaX)
                }
                if (rs.direction.includes('S')) {
                    newHeight = Math.max(MIN_WIDGET_SIZE, rs.initialWidgetSize.height + deltaY)
                }
                if (rs.direction.includes('N')) {
                    newHeight = Math.max(MIN_WIDGET_SIZE, rs.initialWidgetSize.height - deltaY)
                }

                if (rs.direction.includes('W')) {
                    newLeft = rs.initialWidgetPosition.left + (rs.initialWidgetSize.width - newWidth)
                }
                if (rs.direction.includes('N')) {
                    newTop = rs.initialWidgetPosition.top + (rs.initialWidgetSize.height - newHeight)
                }

                const updatedWidget = {...widget, left: newLeft, top: newTop, width: newWidth, height: newHeight}
                dispatch(changeWidgetData({canvasId, changes: updatedWidget}))
            }
            return
        }

        if (!dragState.isDragging || !dragState.dragInfo) {
            return
        }

        const position = getMousePosition({x: e.clientX, y: e.clientY})

        const deltaX = position.x - dragState.dragInfo.startMousePosition.x
        const deltaY = position.y - dragState.dragInfo.startMousePosition.y
        const newPositions = moveWidgetPositions(dragState.dragInfo.draggedWidgets, deltaX, deltaY)

        for (const newPosition of newPositions) {
            const widget = canvasContent.widgets.find(w => w.id === newPosition.id)
            if (widget) {
                const updatedWidget = {...widget, left: newPosition.left, top: newPosition.top}
                dispatch(changeWidgetData({canvasId, changes: updatedWidget}))
            }
        }
    }, [dragState, canvasId, canvasContent.widgets, dispatch, canvasRef, getMousePosition, readOnly])

    const handleCanvasClick = useCallback((position: MousePosition, ctrlKey: boolean) => {
        if (readOnly && mode !== "select") return
        const isClick = canvasService.handleMouseUp(position)

        if (isClick && canvasRef.current) {
            const canvasElementRect = canvasRef.current.getBoundingClientRect()
            const canvasPosition = widgetService.getCanvasPosition(position, canvasElementRect, zoom, scrollPosition)

            if (mode === 'template') {
                onSaveCoordinates(canvasPosition)
                return
            }

            if (mode === 'document') {
                onChooseDocument(canvasPosition, ctrlKey)
                return
            }

            if (mode === 'task') {
                onChooseTask(canvasPosition, ctrlKey)
                return
            }

            processCanvasClick({
                mode,
                canvasPosition,
                ctrlKey,
                canvasId,
                canvasSelection,
                canvasContent,
                layerIds,
                onChangeSelection,
                onStartEdit,
                onChangeTool,
                alignToGrid,
            })
        }
    }, [canvasRef, zoom, scrollPosition, mode, onSaveCoordinates, onChooseDocument, onChooseTask, canvasId, canvasSelection,
        canvasContent, layerIds, onChangeSelection, onStartEdit, onChangeTool, alignToGrid, readOnly])

    const handleMouseUp = useCallback((e: MouseEvent<HTMLDivElement>) => {
        if (e.button !== 0) return
        if (dragState.selectionRect) {
            const intersectingWidgets = getWidgetsInRect(dragState.selectionRect, canvasContent.widgets as Array<Widget>)
            const intersectingLinkIds = getLinksInRect(dragState.selectionRect, canvasContent.widgetLinks, canvasContent.widgets as Array<Widget>)
            onChangeSelection(intersectingWidgets.map(w => w.id))
            setDragState({
                isDragging: false,
                dragInfo: null,
                selectionRect: null,
                resizeState: null,
                linkCreation: dragState.linkCreation,
                mousePosition: null,
                selectedLinkIds: intersectingLinkIds
            })
            return
        }

        if (readOnly && (dragState.resizeState || dragState.isDragging)) return
        if (dragState.resizeState) {
            const rs = dragState.resizeState
            const widget = canvasContent.widgets.find(w => w.id === rs.widgetId)
            if (widget) {
                canvasMoveService.resizeWidgets(canvasId, [{
                    id: widget.id,
                    left: widget.left,
                    top: widget.top,
                    width: widget.width,
                    height: widget.height,
                }], alignToGrid)
            }
            setDragState({
                isDragging: false,
                dragInfo: null,
                selectionRect: null,
                resizeState: null,
                linkCreation: dragState.linkCreation,
                mousePosition: null,
                selectedLinkIds: []
            })
            return
        }

        if (dragState.isDragging && dragState.dragInfo) {
            const dragInfo = dragState.dragInfo
            const widget = canvasContent.widgets.find(w => w.id === dragInfo.widgetId)
            const originalPosition = dragInfo.initialWidgetPosition

            if (widget) {
                const deltaX = widget.left - originalPosition.left
                const deltaY = widget.top - originalPosition.top
                const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

                if (distance < CLICK_DISTANCE_THRESHOLD) {
                    for (const dragged of dragInfo.draggedWidgets) {
                        const draggedWidget = canvasContent.widgets.find(w => w.id === dragged.id)
                        if (draggedWidget) {
                            const restoredWidget = {...draggedWidget, left: dragged.left, top: dragged.top}
                            dispatch(changeWidgetData({canvasId, changes: restoredWidget}))
                        }
                    }
                    setDragState({
                        isDragging: false,
                        dragInfo: null,
                        selectionRect: null,
                        resizeState: null,
                        linkCreation: dragState.linkCreation,
                        mousePosition: null,
                        selectedLinkIds: []
                    })

                    const position: MousePosition = {x: e.clientX, y: e.clientY}
                    handleCanvasClick(position, e.ctrlKey)
                    return
                }

                const positions: Array<{id: string, left: number, top: number}> = []
                for (const dragged of dragInfo.draggedWidgets) {
                    const movedWidget = canvasContent.widgets.find(w => w.id === dragged.id)
                    if (movedWidget) {
                        positions.push({id: movedWidget.id, left: movedWidget.left, top: movedWidget.top})
                    }
                }
                canvasMoveService.moveWidgets(canvasId, positions, alignToGrid)
            }
            setDragState({
                isDragging: false,
                dragInfo: null,
                selectionRect: null,
                resizeState: null,
                linkCreation: null,
                mousePosition: null,
                selectedLinkIds: []
            })
            return
        }

        const position: MousePosition = {x: e.clientX, y: e.clientY}
        handleCanvasClick(position, e.ctrlKey)
    }, [dragState.isDragging,
        dragState.dragInfo,
        dragState.selectionRect,
        getWidgetsInRect,
        onChangeSelection,
        setDragState,
        canvasContent.widgets,
        canvasId,
        dispatch,
        alignToGrid,
        handleCanvasClick, readOnly])

    const startDrag = useCallback((widgetId: string, mousePosition: MousePosition, widgetLeft: number, widgetTop: number) => {
        if (readOnly) return
        canvasService.handleMouseDown(mousePosition)
        const draggedWidgets = getDraggedWidgets(widgetId, canvasSelection, canvasContent.widgets)
        setDragState({
            isDragging: true,
            dragInfo: {
                widgetId,
                startMousePosition: getMousePosition(mousePosition),
                initialWidgetPosition: {left: widgetLeft, top: widgetTop},
                draggedWidgets,
            },
            selectionRect: null,
            resizeState: null,
            linkCreation: dragState.linkCreation,
            mousePosition: null,
            selectedLinkIds: [],
        })
    }, [dragState, canvasSelection, canvasContent.widgets, getMousePosition, readOnly])

    const startResize = useCallback((
        widgetId: string,
        mousePosition: MousePosition,
        widgetLeft: number,
        widgetTop: number,
        widgetWidth: number,
        widgetHeight: number,
        direction: ResizeDirection
    ) => {
        if (readOnly) return
        canvasService.handleMouseDown(mousePosition)
        setDragState({
            isDragging: false,
            dragInfo: null,
            selectionRect: null,
            resizeState: {
                widgetId,
                direction,
                startMousePosition: getMousePosition(mousePosition),
                initialWidgetPosition: {left: widgetLeft, top: widgetTop},
                initialWidgetSize: {width: widgetWidth, height: widgetHeight},
            },
            linkCreation: dragState.linkCreation,
            mousePosition: null,
            selectedLinkIds: [],
        })
    }, [dragState, getMousePosition, readOnly])

    const startLinkCreation = useCallback((widgetId: string, position: LinkPosition) => {
        if (readOnly) return
        setDragState({
            isDragging: false,
            dragInfo: null,
            selectionRect: null,
            resizeState: null,
            linkCreation: {
                isCreating: true,
                sourceWidgetId: widgetId,
                sourcePosition: position,
            },
            mousePosition: null,
            selectedLinkIds: [],
        })
    }, [readOnly])

    const completeLinkCreation = useCallback((targetWidgetId: string, targetPosition: LinkPosition) => {
        if (!readOnly && dragState.linkCreation) {
            const newLink: WidgetLink = {
                id: `link-${Date.now()}`,
                startWidgetId: dragState.linkCreation.sourceWidgetId,
                endWidgetId: targetWidgetId,
                data: {
                    startPosition: dragState.linkCreation.sourcePosition,
                    endPosition: targetPosition,
                },
                draft: true,
            }
            messagesService.send<CreateWidgetLink>('canvas.create-widget-link', {
                canvasId,
                startWidgetId: dragState.linkCreation.sourceWidgetId,
                endWidgetId: targetWidgetId,
                data: newLink.data,
            })
            dispatch(addWidgetLink({canvasId, changes: newLink}))
            setDragState({
                isDragging: false,
                dragInfo: null,
                selectionRect: null,
                resizeState: null,
                linkCreation: null,
                mousePosition: null,
                selectedLinkIds: [],
            })
        }
    }, [dragState.linkCreation, canvasId, dispatch, readOnly])

    useEffect(() => {
        if (!readOnly) return
        setDragState(previous => ({...previous, isDragging: false, dragInfo: null,
            resizeState: null, linkCreation: null, mousePosition: null}))
    }, [readOnly, canvasId])

    return {
        dragState,
        setDragState: setDragState,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        startDrag,
        startResize,
        startLinkCreation,
        completeLinkCreation,
    }
}
